/**
 * PostHog + Meta Pixel Event Tracking for Shopify
 *
 * E-commerce:  product_viewed, add_to_cart, checkout_started, order_completed
 * Engagement:  section_viewed, animation_engaged, image_clicked, cta_clicked
 * UX:          button_clicked, form_submitted, search_submitted
 * Navigation:  scroll_depth_reached, outbound_link_clicked
 *
 * Meta Pixel:  ViewContent, AddToCart, InitiateCheckout, Purchase
 *              — fired in sync with PostHog with event_id for CAPI deduplication
 */

(function () {
  if (typeof window.posthog === 'undefined') return;

  // Defer no-críticos (observers de telemetría) fuera de la ventana de LCP/primer
  // INP. requestIdleCallback con fallback setTimeout para Safari iOS.
  const idle = window.requestIdleCallback
    ? (fn) => window.requestIdleCallback(fn, { timeout: 2000 })
    : (fn) => setTimeout(fn, 1);

  // ── Shared context ─────────────────────────────────────────────────────────
  const template = document.querySelector('#MainContent')?.dataset?.template || '';
  const pageUrl  = window.location.href;
  const pagePath = window.location.pathname;

  // ── Meta Pixel helpers ─────────────────────────────────────────────────────
  // Generates a unique event ID for CAPI deduplication (browser + server same ID)
  function genEventId() {
    return 'ev_' + Date.now() + '_' + Math.random().toString(36).slice(2, 9);
  }

  // Fires a Meta Pixel event safely, ignoring if fbq is blocked
  function fbTrack(eventName, params, eventId) {
    try {
      if (typeof window.fbq !== 'function') return;
      window.fbq('track', eventName, params, { eventID: eventId });
    } catch (e) {}
  }

  function sectionLabel(el) {
    return (
      el.dataset.s9Section ||
      el.dataset.h9 ||
      el.dataset.sectionId ||
      el.dataset.section ||
      el.id ||
      el.dataset.componentName ||
      el.getAttribute('aria-label') ||
      el.className.split(' ').find((c) => c.length > 3 && !c.includes('shopify')) ||
      'unknown'
    );
  }

  function nearestSection(el) {
    return el?.closest('[data-section-id], section, [id]') || null;
  }

  // ── Product Viewed → Meta ViewContent ─────────────────────────────────────
  if (template.startsWith('product')) {
    const productData = window.ShopifyAnalytics?.meta?.product;
    if (productData) {
      const eventId = genEventId();
      const price   = productData.variants?.[0]?.price / 100;
      const currency = window.Shopify?.currency?.active || 'USD';

      posthog.capture('product_viewed', {
        product_id:     productData.id,
        product_name:   productData.title,
        product_type:   productData.type,
        product_vendor: productData.vendor,
        price,
        currency,
        url:            pageUrl,
        meta_event_id:  eventId,
      });

      fbTrack('ViewContent', {
        content_ids:  [String(productData.id)],
        content_name: productData.title,
        content_type: 'product',
        value:        price,
        currency,
      }, eventId);
    }
  }

  // ── Add to Cart → Meta AddToCart ───────────────────────────────────────────
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const [resource, config] = args;
    const url = typeof resource === 'string' ? resource : resource?.url || '';

    if (url.includes('/cart/add')) {
      try {
        const body = config?.body;
        let items = [];

        if (typeof body === 'string') {
          try {
            const parsed = JSON.parse(body);
            items = parsed.items || (parsed.id ? [parsed] : []);
          } catch {
            const params = new URLSearchParams(body);
            if (params.get('id')) {
              items = [{ id: params.get('id'), quantity: params.get('quantity') || 1 }];
            }
          }
        } else if (body instanceof FormData) {
          if (body.get('id')) {
            items = [{ id: body.get('id'), quantity: body.get('quantity') || 1 }];
          }
        }

        const response = await originalFetch.apply(this, args);
        response.clone().json().then((data) => {
          const addedItems = data.items || (data.id ? [data] : items);
          addedItems.forEach((item) => {
            const eventId  = genEventId();
            const price    = (item.price || item.final_price || 0) / 100;
            const currency = window.Shopify?.currency?.active || 'USD';

            posthog.capture('add_to_cart', {
              product_id:    item.product_id || item.id,
              variant_id:    item.variant_id || item.id,
              product_name:  item.product_title || item.title,
              variant_name:  item.variant_title,
              price,
              quantity:      item.quantity || 1,
              currency,
              sku:           item.sku,
              meta_event_id: eventId,
            });

            fbTrack('AddToCart', {
              content_ids:  [String(item.product_id || item.id)],
              content_name: item.product_title || item.title,
              content_type: 'product',
              value:        price * (item.quantity || 1),
              currency,
            }, eventId);
          });
        }).catch(() => {});
        return response;
      } catch {
        return originalFetch.apply(this, args);
      }
    }

    return originalFetch.apply(this, args);
  };

  // ── Checkout Started → Meta InitiateCheckout ───────────────────────────────
  document.addEventListener('click', function (e) {
    const el = e.target.closest('[href*="/checkout"], [name="checkout"], [data-action="checkout"]');
    if (!el) return;

    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        const eventId  = genEventId();
        const currency = cart.currency;
        const value    = cart.total_price / 100;

        posthog.capture('checkout_started', {
          cart_total:    value,
          item_count:    cart.item_count,
          currency,
          meta_event_id: eventId,
          items: (cart.items || []).map((item) => ({
            product_id:   item.product_id,
            variant_id:   item.variant_id,
            product_name: item.product_title,
            variant_name: item.variant_title,
            price:        item.price / 100,
            quantity:     item.quantity,
            sku:          item.sku,
          })),
        });

        fbTrack('InitiateCheckout', {
          content_ids: (cart.items || []).map((i) => String(i.product_id)),
          num_items:   cart.item_count,
          value,
          currency,
        }, eventId);
      })
      .catch(() => {});
  }, true);

  // ── Order Completed → Meta Purchase ───────────────────────────────────────
  if (window.Shopify?.checkout) {
    const checkout = window.Shopify.checkout;
    const eventId  = genEventId();
    const revenue  = parseFloat(checkout.total_price);
    const currency = checkout.currency;

    posthog.capture('order_completed', {
      order_id:      checkout.order_id,
      order_number:  checkout.order_number,
      revenue,
      subtotal:      parseFloat(checkout.subtotal_price),
      shipping:      parseFloat(checkout.shipping_rate?.price || 0),
      tax:           parseFloat(checkout.tax_price),
      currency,
      email:         checkout.email,
      items: (checkout.line_items || []).map((item) => ({
        product_id:   item.product_id,
        variant_id:   item.variant_id,
        product_name: item.title,
        price:        parseFloat(item.price),
        quantity:     item.quantity,
        sku:          item.sku,
      })),
    });
    // Meta Purchase lo dispara el canal nativo Facebook & Instagram de Shopify.
    // No fbTrack('Purchase') aqui — duplicaba el evento (CAPI no deduplica entre
    // event IDs distintos) e inflaba ventas/CPA x2 en Ads Manager.
  }

  // ── Search Submitted ───────────────────────────────────────────────────────
  document.addEventListener('submit', function (e) {
    const form = e.target.closest('form[action*="/search"]');
    if (!form) return;
    const q = form.querySelector('[name="q"]')?.value;
    if (q) posthog.capture('search_submitted', { query: q, page_path: pagePath });
  });

  // ── Button Clicked ─────────────────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    const btn = e.target.closest(
      'button, [role="button"], input[type="submit"], input[type="button"]'
    );
    if (!btn) return;

    const skipPatterns = [/checkout/i, /add.?to.?cart/i];
    const rawText = (
      btn.textContent ||
      btn.value ||
      btn.getAttribute('aria-label') ||
      btn.getAttribute('title') ||
      ''
    ).trim();

    if (!rawText || skipPatterns.some((p) => p.test(rawText))) return;

    const section = nearestSection(btn);
    posthog.capture('button_clicked', {
      button_text:   rawText.slice(0, 100),
      button_id:     btn.id || null,
      button_type:   btn.type || btn.getAttribute('role') || null,
      section:       section ? sectionLabel(section) : null,
      page_path:     pagePath,
      page_template: template,
    });
  });

  // ── Landing CTAs (sleep-9 / glp-9 / home-9) — captura fiable por data-attr ──
  // Fuente única para los CTA de las landings custom. El listener genérico de
  // abajo los ignora para no duplicar el evento.
  function ctaProductFromHref(href) {
    if (/parche-glp-1/.test(href)) return 'glp';
    if (/parches-para-dormir/.test(href)) return 'sleep';
    return null;
  }
  document.addEventListener('click', function (e) {
    const cta = e.target.closest('[data-s9-cta], .h9-cta, .s9-cta, .s9-sticky-cta');
    if (!cta) return;
    const link = cta.matches('a[href]') ? cta : cta.closest('a[href]');
    const href = (link && link.href) || '';
    posthog.capture('cta_clicked', {
      cta_id:        cta.getAttribute('data-s9-cta') || cta.getAttribute('data-h9-cta') || null,
      cta_text:      (cta.textContent || '').trim().slice(0, 80),
      cta_url:       href || null,
      cta_product:   ctaProductFromHref(href),
      cta_section:   sectionLabel(nearestSection(cta) || cta),
      page_path:     pagePath,
      page_template: template,
    });
  });

  // ── CTA Clicked (genérico, resto del sitio) ─────────────────────────────────
  document.addEventListener('click', function (e) {
    const cta = e.target.closest('a[href]');
    if (!cta) return;
    // Los CTA de landing ya se capturan arriba — evitar doble conteo
    if (cta.closest('[data-s9-cta], .h9-cta, .s9-cta, .s9-sticky-cta')) return;

    const href = cta.href || '';
    const text = (cta.textContent || cta.getAttribute('aria-label') || '').trim();
    const isInternalNav = href.includes(window.location.hostname) || href.startsWith('/');

    if (!isInternalNav || !text) return;

    const ctaPatterns = /compra|buy|shop|order|get|start|try|ver|view|add|subscribe|join|learn|more|ahora|now|producto|product/i;
    if (!ctaPatterns.test(text) && !ctaPatterns.test(href)) return;

    const section = nearestSection(cta);
    posthog.capture('cta_clicked', {
      cta_text:      text.slice(0, 100),
      cta_url:       href,
      cta_section:   section ? sectionLabel(section) : null,
      page_path:     pagePath,
      page_template: template,
    });
  });

  // ── Form Submitted ─────────────────────────────────────────────────────────
  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!form || form.action?.includes('/search')) return;

    const sensitiveFields = /password|card|cvv|ssn|secret/i;
    const fields = Array.from(form.elements)
      .filter((el) => el.name && !sensitiveFields.test(el.name) && el.type !== 'hidden')
      .map((el) => el.name);

    posthog.capture('form_submitted', {
      form_id:       form.id || null,
      form_action:   form.action || null,
      form_method:   form.method || 'get',
      field_names:   fields,
      page_path:     pagePath,
      page_template: template,
    });
  });

  // ── EasySell COD Form Tracking ─────────────────────────────────────────────
  // EasySell's "submit" button does window.location → wa.me (no DOM submit
  // event), so the generic listener above never fires. We hook the lifecycle
  // by hand: form open (MutationObserver), per-field interactions, submit
  // click (capture phase + sendBeacon to survive the redirect), abandonment.
  if (template.startsWith('product')) {
    const COD_FIELD_SELECTORS = {
      nombre:       'input[name*="first" i], input[name*="nombre" i], input[placeholder*="Nombre" i]:not([placeholder*="Apellido" i])',
      apellido:     'input[name*="last" i], input[name*="apellido" i], input[placeholder*="Apellido" i]',
      celular:      'input[name*="phone" i], input[type="tel"], input[name*="celular" i]',
      direccion:    'input[name*="address" i], input[name*="direccion" i], input[placeholder*="Dirección" i]',
      referencias:  'input[name*="ref" i], textarea[name*="ref" i], input[placeholder*="Referencias" i]',
      provincia:    'select[name*="province" i], select[name*="provincia" i], select[name*="state" i]',
      ciudad:       'select[name*="city" i], select[name*="ciudad" i]',
    };

    // The EasySell app embed injects multiple containers. The CTA button
    // (shopify-block-…) is in the DOM from the start; the real form
    // (#es-form inside #easysell-modal) only appears once the user clicks
    // a trigger. Priority order picks the form first, falls back to modal.
    const easySellRoot = () =>
      document.querySelector('form.es-form, #es-form') ||
      document.querySelector('#easysell-modal') ||
      document.querySelector('#es-form-container') ||
      // Last resort: any container that holds the known phone input
      (() => {
        const phone = document.querySelector(COD_FIELD_SELECTORS.celular);
        return phone ? phone.closest('form, [role="dialog"], section, div[class*="modal" i]') : null;
      })();

    const isVisible = (el) => {
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      const style = getComputedStyle(el);
      // EasySell modal is in DOM but offscreen/clipped when closed.
      // Width+height > some real threshold AND visible computed styles.
      return rect.width > 50 && rect.height > 50 &&
             style.visibility !== 'hidden' && style.display !== 'none' &&
             parseFloat(style.opacity || '1') > 0.1;
    };

    const productData = window.ShopifyAnalytics?.meta?.product;
    const cartValue = productData?.variants?.[0]?.price ? productData.variants[0].price / 100 : null;
    const currency = window.Shopify?.currency?.active || 'USD';
    const productId = productData?.id ? String(productData.id) : null;

    const state = {
      tracked: false,
      submitted: false,
      openedAt: null,
      fieldsCompleted: [],
      lastFieldFocused: null,
      submitEventId: null,
    };

    window.__agCodLastCtaTrigger = null;
    let codObserver = null; // se asigna en idle; trackOpen lo desconecta al abrir el form

    function fieldNameFor(el) {
      for (const [name, sel] of Object.entries(COD_FIELD_SELECTORS)) {
        if (el.matches(sel)) return name;
      }
      return el.name || el.placeholder || 'unknown';
    }

    function collectFieldState(root) {
      const filled = [];
      const missing = [];
      for (const [name, sel] of Object.entries(COD_FIELD_SELECTORS)) {
        const el = root.querySelector(sel);
        if (!el) continue;
        const v = (el.value || '').trim();
        if (v) filled.push(name);
        else if (el.required) missing.push(name);
      }
      return { filled, missing };
    }

    function attachFieldListeners(root) {
      Object.entries(COD_FIELD_SELECTORS).forEach(([name, sel]) => {
        const el = root.querySelector(sel);
        if (!el || el.dataset.phCodBound) return;
        el.dataset.phCodBound = '1';

        el.addEventListener('focus', () => {
          state.lastFieldFocused = name;
          posthog.capture('cod_field_focused', {
            field_name:   name,
            page_path:    pagePath,
          });
        });

        el.addEventListener('blur', () => {
          const value = (el.value || '').trim();
          if (!value) return;
          const isValid = typeof el.checkValidity === 'function' ? el.checkValidity() : true;
          if (!state.fieldsCompleted.includes(name)) state.fieldsCompleted.push(name);

          posthog.capture('cod_field_completed', {
            field_name:    name,
            value_length:  value.length,
            is_valid:      isValid,
            page_path:     pagePath,
          });

          if (!isValid) {
            posthog.capture('cod_field_invalid', {
              field_name:  name,
              error_type:  el.validationMessage || 'unknown',
              page_path:   pagePath,
            });
          }

          // High-intent signal: phone completed → Meta Lead
          if (name === 'celular' && isValid) {
            const eventId = genEventId();
            posthog.capture('cod_phone_captured', { meta_event_id: eventId });
            fbTrack('Lead', {
              content_ids:  productId ? [productId] : [],
              content_name: productData?.title,
              value:        cartValue,
              currency,
            }, eventId);
          }
        });

        if (el.tagName === 'SELECT') {
          el.addEventListener('change', () => {
            const value = el.value || el.options?.[el.selectedIndex]?.text;
            if (!value) return;
            const evt = name === 'provincia' ? 'cod_province_selected' :
                        name === 'ciudad'    ? 'cod_city_selected' : null;
            if (evt) {
              posthog.capture(evt, {
                value,
                page_path: pagePath,
              });
            }
          });
        }
      });
    }

    function attachSubmitListener(root) {
      const candidates = Array.from(root.querySelectorAll('button, input[type="submit"]'));
      const submitBtn = candidates.find((b) => /toca aqui|comprar|ordenar|finalizar|enviar/i.test(
        (b.textContent || b.value || '').trim()
      ));
      if (!submitBtn || submitBtn.dataset.phCodBound) return;
      submitBtn.dataset.phCodBound = '1';

      submitBtn.addEventListener('click', () => {
        if (state.submitted) return;
        state.submitted = true;
        root.dataset.phSubmitted = '1';

        const eventId = genEventId();
        state.submitEventId = eventId;
        const fields = collectFieldState(root);

        posthog.capture('cod_submit_clicked', {
          cart_value:               cartValue,
          currency,
          fields_filled:            fields.filled,
          fields_missing:           fields.missing,
          field_completion_order:   state.fieldsCompleted,
          time_to_submit_ms:        Math.round(performance.now() - (state.openedAt || 0)),
          page_path:                pagePath,
          meta_event_id:            eventId,
        });

        // Custom event — NO Purchase. En COD el "submit" es un lead caliente,
        // la venta real ocurre cuando confirman por WhatsApp y se entrega.
        // Disparar Purchase aqui inflaba ventas en Ads Manager.
        fbTrack('SubmitApplication', {
          content_ids:  productId ? [productId] : [],
          content_name: productData?.title,
          content_type: 'product',
          value:        cartValue,
          currency,
        }, eventId);

        // sendBeacon survives the imminent window.location → wa.me redirect.
        // PostHog's own queue uses fetch+keepalive which usually works, but
        // we double up because a missed Purchase event costs real money.
        try {
          if (navigator.sendBeacon) {
            const payload = new Blob(
              [JSON.stringify({ event: 'cod_submit_clicked', eventId, cartValue, ts: Date.now() })],
              { type: 'application/json' }
            );
            navigator.sendBeacon('/apps/posthog-beacon', payload);
          }
        } catch (e) {}
      }, true); // capture phase → fires BEFORE EasySell's own click handler
    }

    function trackOpen(root) {
      if (state.tracked) return;
      state.tracked = true;
      if (codObserver) codObserver.disconnect(); // form abierto → dejar de observar el body (costo cero el resto de la sesión)
      state.openedAt = performance.now();

      const eventId = genEventId();
      posthog.capture('cod_form_opened', {
        trigger_source:    window.__agCodLastCtaTrigger || 'unknown',
        page_template:     template,
        time_to_open_ms:   Math.round(state.openedAt),
        meta_event_id:     eventId,
      });

      fbTrack('InitiateCheckout', {
        content_ids:  productId ? [productId] : [],
        content_name: productData?.title,
        num_items:    1,
        value:        cartValue,
        currency,
      }, eventId);

      attachFieldListeners(root);
      attachSubmitListener(root);
    }

    // Re-attach listeners if EasySell re-renders the form (open → close → open).
    function probe() {
      const root = easySellRoot();
      if (!root) return;
      if (isVisible(root)) trackOpen(root);
      else {
        // Form is in DOM but hidden — still bind so we catch the first focus
        attachFieldListeners(root);
      }
    }

    // probe() coalescido a ≤1 reflow por frame.
    let probeScheduled = false;
    function scheduleProbe() {
      if (probeScheduled) return;
      probeScheduled = true;
      requestAnimationFrame(() => { probeScheduled = false; probe(); });
    }
    // Ráfaga de probes tras un click de trigger, para cazar el modal mientras
    // se abre (cobertura extra además del observer de abajo).
    function probeBurst() { [80, 250, 500, 900, 1500].forEach((ms) => setTimeout(probe, ms)); }

    // 1. Trigger source attribution
    document.addEventListener('click', (e) => {
      const t = e.target.closest('#StickyCtaBtn, [id^="es-"], [class*="es-sticky"], [class*="es-button"], [onclick*="agTriggerEasySell"], [data-action*="checkout"], a[href*="/cart"]');
      if (!t) return;
      const id = t.id || '';
      const cls = (t.className || '').toString();
      window.__agCodLastCtaTrigger =
        id === 'StickyCtaBtn'        ? 'sticky_cta' :
        /es-sticky/.test(cls)        ? 'easysell_sticky' :
        /es-button/.test(cls)        ? 'easysell_inline' :
        /agTriggerEasySell/.test(t.outerHTML) ? 'manual_trigger' :
                                       'cta_button';
      probeBurst();
    }, true);

    // 2. MutationObserver para cazar el form (inyectado async o mostrado al abrir).
    //    Detección IDÉNTICA a la versión probada en producción (childList + attrs),
    //    pero el callback va rAF-debounced (scheduleProbe → ≤1 reflow/frame en vez
    //    de N por cada toggle de clase reveal al scrollear) y trackOpen() hace
    //    disconnect() al abrir → costo cero el resto de la sesión. Setup en idle.
    idle(function () {
      codObserver = new MutationObserver(scheduleProbe);
      codObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'aria-hidden'],
      });
      probe(); // por si EasySell ya estaba presente
    });

    // 3. Abandonment on unload — only fire if user opened form and didn't submit
    const fireAbandon = () => {
      if (!state.tracked || state.submitted) return;
      if (state.fieldsCompleted.length === 0) return;
      try {
        const payload = {
          event: 'cod_form_abandoned',
          fields_completed:    state.fieldsCompleted,
          last_field_focused:  state.lastFieldFocused,
          time_in_form_ms:     Math.round(performance.now() - (state.openedAt || 0)),
          page_path:           pagePath,
        };
        // posthog.capture has internal keepalive but beacon is more reliable on unload
        posthog.capture('cod_form_abandoned', payload);
        if (navigator.sendBeacon) {
          navigator.sendBeacon('/apps/posthog-beacon', new Blob(
            [JSON.stringify(payload)], { type: 'application/json' }
          ));
        }
      } catch (e) {}
    };
    window.addEventListener('pagehide', fireAbandon);
    window.addEventListener('beforeunload', fireAbandon);
  }

  // ── Landings s9 — elementos que no tenían tracking (mapeados 2026-06-09) ──
  // Aplica a sleep-9 y glp-9 (comparten clases .s9-*). Guards por selector:
  // en páginas sin estos elementos no se dispara nada.

  // FAQ y dropdown de ingredientes del hero son <details>; "toggle" no
  // burbujea, así que se escucha en capture a nivel de documento.
  document.addEventListener('toggle', function (e) {
    const el = e.target;
    if (!(el instanceof Element) || !el.open) return;
    if (el.matches('.s9-faq__item')) {
      posthog.capture('faq_opened', {
        faq_question:  (el.querySelector('.s9-faq__q, summary')?.textContent || '').trim().slice(0, 120),
        page_path:     pagePath,
        page_template: template,
      });
    } else if (el.matches('.s9-hero__ing')) {
      posthog.capture('ingredients_dropdown_opened', {
        page_path:     pagePath,
        page_template: template,
      });
    }
  }, true);

  // Navegación del carrusel de videos UGC (slpcar): flechas y dots
  document.addEventListener('click', function (e) {
    const arrow = e.target.closest('.slpcar__arrow');
    const dot   = !arrow && e.target.closest('.slpcar__dots button, .slpcar__dots [role="button"]');
    if (!arrow && !dot) return;
    posthog.capture('video_nav_clicked', {
      nav_type:      arrow ? (/--next/.test(arrow.className) ? 'next' : 'prev') : 'dot',
      page_path:     pagePath,
      page_template: template,
    });
  });

  // Qué video UGC llegan a ver (slide ≥60% visible, una vez por pageview)
  const videoSlides = Array.from(document.querySelectorAll('.slpcar__slide'));
  if (videoSlides.length && 'IntersectionObserver' in window) idle(function () {
    const seenSlides = new Set();
    const slideObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const idx = videoSlides.indexOf(entry.target);
        if (seenSlides.has(idx)) return;
        seenSlides.add(idx);
        const src = entry.target.querySelector('video')?.getAttribute('src') || '';
        posthog.capture('video_slide_viewed', {
          video_index:   idx,
          video_name:    (src.split('?')[0].split('/').pop()) || null,
          page_path:     pagePath,
          page_template: template,
        });
        slideObserver.unobserve(entry.target);
      });
    }, { threshold: 0.6 });
    videoSlides.forEach((s) => slideObserver.observe(s));
  });

  // Sticky CTA: cuándo APARECE → MOVIDO a sleep9/glp9-custom.js.
  // Antes un MutationObserver observaba body[class] (otro observer sobre el body)
  // esperando body.s9-es-on. Ahora el propio custom.js — que AÑADE esa clase al
  // pasar el hero — dispara posthog.capture('sticky_cta_shown', ...) en ese mismo
  // instante (guard window.__s9StickyShown). Un observer menos sobre el body.
  // (perf 2026-06-17)

  // ── Scroll Depth → Meta también a 75% ─────────────────────────────────────
  const scrollMilestones = [25, 50, 75, 100];
  const firedMilestones  = new Set();

  function getScrollDepth() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    return docHeight <= 0 ? 100 : Math.round((scrollTop / docHeight) * 100);
  }

  function processScrollDepth() {
    const depth = getScrollDepth();
    scrollMilestones.forEach((milestone) => {
      if (depth >= milestone && !firedMilestones.has(milestone)) {
        firedMilestones.add(milestone);

        posthog.capture('scroll_depth_reached', {
          depth_percent:  milestone,
          page_path:      pagePath,
          page_template:  template,
        });

        // Meta: fire a custom event at 75% — indica intención alta de compra
        if (milestone === 75) {
          fbTrack('CustomEvent', { event_name: 'PageScrolled75', page_path: pagePath }, genEventId());
        }
      }
    });
    // Limpiar al cruzar el último milestone (con la MISMA referencia registrada).
    if (firedMilestones.size === scrollMilestones.length) {
      window.removeEventListener('scroll', onScrollRaf);
    }
  }

  // Throttle con rAF: colapsa decenas de eventos scroll/seg en ≤1 lectura de
  // layout por frame (antes hacía reflow en cada evento = INP alto en móvil).
  let scrollTicking = false;
  function onScrollRaf() {
    if (scrollTicking) return;
    scrollTicking = true;
    requestAnimationFrame(() => { scrollTicking = false; processScrollDepth(); });
  }

  window.addEventListener('scroll', onScrollRaf, { passive: true });
  processScrollDepth();

  // ── Section Viewed ─────────────────────────────────────────────────────────
  const viewedSections = new Set();
  // Lista precalculada UNA vez (antes se re-consultaba el DOM completo por cada
  // sección vista, solo para sacar el índice). Setup diferido a idle.
  const sectionEls = Array.from(document.querySelectorAll('[data-section-id], section'));
  if (sectionEls.length && 'IntersectionObserver' in window) idle(function () {
    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el    = entry.target;
          const label = sectionLabel(el);
          if (!viewedSections.has(label)) {
            viewedSections.add(label);
            posthog.capture('section_viewed', {
              section_name:  label,
              section_index: sectionEls.indexOf(el),
              page_path:     pagePath,
              page_template: template,
            });
          }
          sectionObserver.unobserve(el); // una vista por sección — dejar de observar
        });
      },
      // Banda central del viewport en vez de threshold 0.5: una sección más alta
      // que la pantalla nunca alcanza ratio 0.5 (en sleep-9 "how" registraba 12
      // views vs 61 de "morning" que va DESPUÉS — subconteo, no comportamiento).
      // Con rootMargin -35% top/bottom + threshold 0, dispara cuando la sección
      // cruza el 30% central de la pantalla — funciona igual para altas y cortas.
      { threshold: 0, rootMargin: '-35% 0px -35% 0px' }
    );
    sectionEls.forEach((el) => sectionObserver.observe(el));
  });

  // ── Animation Engaged ──────────────────────────────────────────────────────
  const animationEls = document.querySelectorAll(
    '[data-animation], [data-animate], .animate, .fade-in, .slide-in, ' +
    '[class*="anim"], [class*="fade"], [class*="slide-"], [class*="reveal"]'
  );
  if (animationEls.length && 'IntersectionObserver' in window) idle(function () {
    const animationObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const label =
            el.dataset.animationName ||
            el.dataset.animation ||
            el.id ||
            el.className.split(' ').find((c) => c.includes('anim') || c.includes('fade') || c.includes('slide')) ||
            'animated_element';

          posthog.capture('animation_engaged', {
            animation_name: label,
            section:        sectionLabel(nearestSection(el) || el),
            page_path:      pagePath,
            page_template:  template,
          });
          animationObserver.unobserve(el);
        });
      },
      { threshold: 0.3 }
    );
    animationEls.forEach((el) => animationObserver.observe(el));
  });

  // ── Image Clicked ──────────────────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    const img = e.target.closest('img, picture, [role="img"]');
    if (!img) return;
    if (img.width < 60 || img.height < 60) return;

    const src     = img.src || img.currentSrc || img.dataset.src || '';
    const section = nearestSection(img);

    posthog.capture('image_clicked', {
      image_src:     src.split('?')[0].split('/').pop(),
      image_alt:     img.alt || null,
      image_width:   img.naturalWidth || img.width,
      section:       section ? sectionLabel(section) : null,
      page_path:     pagePath,
      page_template: template,
    });
  });

  // ── Outbound Link Clicked ──────────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    const link = e.target.closest('a[href]');
    if (!link) return;

    const href = link.href || '';
    const isOutbound =
      href.startsWith('http') &&
      !href.includes(window.location.hostname) &&
      !href.startsWith('javascript');

    if (!isOutbound) return;

    posthog.capture('outbound_link_clicked', {
      link_url:      href,
      link_text:     (link.textContent || link.getAttribute('aria-label') || '').trim().slice(0, 100),
      link_domain:   new URL(href).hostname,
      page_path:     pagePath,
      page_template: template,
    });
  });

})();
